import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppAttachRules - what the file row under the message box SAYS (audit 2026-10-12). Pure: no DOM, no transport.
//
// The row was the browser's own file control and a bare checkbox; the refusal for a file that is too large was only
// said after Send was pressed, in the words "between 1 byte and 8 MiB". Now a chosen file is named with its size at
// once, a file that cannot go is refused the moment it is chosen (and taken out of the picker), and the voice-note
// choice is offered only for audio. `AppAttachmentComposer` is the browser half.
//
// A second consumer: any composer that shows one chosen file. The one product fact - the size limit - is a parameter.

export class AppAttachRules {
    /** Shown when the picker's file is empty or over the limit. ⛔ Fate 1: a sentence under the box, never a report. */
    public static TooBigWhy = (MaxBytes: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "That file is empty or larger than " + AppAttachRules.SizeWords(MaxBytes) + ", so it cannot be sent. Choose another one.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAttachRules.TooBigWhy");
            return "";
        }
    };

    public static readonly VoiceWhy: string = "A voice note is an audio file with no words. Clear the message box, or untick the voice note.";

    /** May a file of this size go at all? */
    public static SizeOk = (Bytes: number, MaxBytes: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return typeof Bytes === "number" && isFinite(Bytes) === true && Bytes > 0 && Bytes <= MaxBytes;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAttachRules.SizeOk");
            return false;
        }
    };

    /** "640 bytes", "17 KB", "1.2 MB", "8 MB" - the units people say (1 KB = 1024 bytes, as the limit is counted). */
    public static SizeWords = (Bytes: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (typeof Bytes !== "number" || isFinite(Bytes) === false || Bytes < 0) { return ""; }
            if (Bytes < 1024) { return String(Math.round(Bytes)) + " bytes"; }
            if (Bytes < 1024 * 1024) { return String(Math.max(1, Math.round(Bytes / 1024))) + " KB"; }
            const Mb: number = Math.round(Bytes / (1024 * 1024) * 10) / 10;
            return String(Mb) + " MB";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAttachRules.SizeWords");
            return "";
        }
    };

    /** "invoice.pdf · 17 KB" - text for `textContent`, never markup. A nameless file is "File". */
    public static Shown = (Name: string, Bytes: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Title: string = nz(Name).trim();
            if (Title.length === 0) { Title = "File"; }
            const Size: string = AppAttachRules.SizeWords(Bytes);
            if (Size.length === 0) { return Title; }
            return Title + " · " + Size;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAttachRules.Shown");
            return "";
        }
    };

    /** Is the voice-note choice offered for this file? Only audio can be one. */
    public static IsAudio = (Mime: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return nz(Mime).toLowerCase().indexOf("audio/") === 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAttachRules.IsAudio");
            return false;
        }
    };
    /**
     * The name a PASTED file goes out under. A screenshot arrives with no real name ("" or "image.png") and becomes
     * screenshot-<yyyyMMdd-HHmmss>.<ext> in the browser's clock; a named file (copied in Explorer) keeps its name.
     * REUSE-JUSTIFIED: same rule as the shared `ChatAttachmentTray.NameScreenshot`, which imports kalmstromShared4 -
     * this app never loads it (it would replace the app's own error reporter); this copy is pure and takes strings.
     */
    public static PastedName = (Name: string, Mime: string, When: Date): string => {
        if (getCoreUtils().KErrorMode === true) { return nz(Name); }
        try {
            const Type: string = nz(Mime).toLowerCase();
            const Base: string = nz(Name).trim();
            const Lower: string = Base.toLowerCase();
            if (Type.indexOf("image/") !== 0) { return Base.length > 0 ? Base : "file"; }
            if (Lower !== "" && Lower !== "image" && Lower.indexOf("image.") !== 0) { return Base; }
            let Ext: string = Type.substring(6);
            if (Ext === "jpeg") { Ext = "jpg"; }
            if (/^[a-z0-9]+$/.test(Ext) === false) { Ext = "png"; }
            const Two = (N: number): string => (N < 10 ? "0" : "") + String(N);
            const Stamp: string = String(When.getFullYear()) + Two(When.getMonth() + 1) + Two(When.getDate()) + "-"
                + Two(When.getHours()) + Two(When.getMinutes()) + Two(When.getSeconds());
            return "screenshot-" + Stamp + "." + Ext;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAttachRules.PastedName");
            return nz(Name);
        }
    };
}
